/**
 * AP-46 11.2, BD-43 — **Dashboard der Baustellendokumente**, die Startseite des Hauptmenüpunkts
 * (`/site-docs/dashboard`). Jede Kachel ist ein Filter der Liste (AP-40 6.4).
 */
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { HardHat } from 'lucide-react'
import { SITE_DOC_KINDS, type SiteDocKind, type SiteDocStatus } from '@symphenia/shared-types'
import { DashboardKopf, DashboardListe, Kachel, KachelRaster, useDashboard } from '@/components/dashboard'
import { fmtDate } from '@/lib/format-date'
import { pageWide } from '@/lib/form-styles'
import { fetchSiteDocsDashboard } from '../api/site-docs.api'
import { StatusPill } from '../components/StatusPill'

interface Zuletzt {
  id: string
  kind: SiteDocKind
  number: string | null
  title: string
  status: SiteDocStatus
  projectLabel: string
  documentDate: string
  createdByName: string
}

export default function SiteDocsDashboardPage() {
  const { t } = useTranslation()
  const { daten, fehler, kachel, bereich, setBereich } = useDashboard('site_docs', (scope) => fetchSiteDocsDashboard(scope))

  return (
    <div className={pageWide}>
      <DashboardKopf
        symbol={HardHat}
        titel={t('siteDocs.title')}
        untertitel={t('siteDocs.dashboard.subtitle')}
        bereich={bereich}
        onBereich={setBereich}
        umschalter={!!daten?.scopeSwitch}
        stand={daten?.stand}
      />

      {fehler && <div className="text-sm text-destructive">{fehler}</div>}

      <KachelRaster>
        <Kachel titel={t('siteDocs.dashboard.onDevice')} ton="warnung" kachel={kachel('onDevice')} hinweis={t('siteDocs.dashboard.onDeviceHint')} />
        <Kachel titel={t('siteDocs.dashboard.awaiting')} kachel={kachel('awaiting')} hinweis={t('siteDocs.dashboard.awaitingHint')} />
        <Kachel titel={t('siteDocs.dashboard.copyOpen')} ton="warnung" kachel={kachel('copyOpen')} hinweis={t('siteDocs.dashboard.copyOpenHint')} />
        <Kachel titel={t('siteDocs.dashboard.filesMissing')} ton="gefahr" kachel={kachel('filesMissing')} hinweis={t('siteDocs.dashboard.filesMissingHint')} />
        <Kachel titel={t('siteDocs.dashboard.drafts')} kachel={kachel('drafts')} hinweis={t('siteDocs.dashboard.draftsHint')} />
        {daten?.tiles?.billable && <Kachel titel={t('siteDocs.dashboard.billable')} kachel={kachel('billable')} hinweis={t('siteDocs.dashboard.billableHint')} />}
        {SITE_DOC_KINDS.map((k) => (
          <Kachel key={k} titel={t(`siteDocs.kinds.${k}`)} kachel={kachel(`kind_${k}`)} hinweis={t('siteDocs.dashboard.kindHint')} />
        ))}
      </KachelRaster>

      <DashboardListe<Zuletzt>
        titel={t('siteDocs.dashboard.recent')}
        zeilen={daten?.lists?.recent as Zuletzt[] | undefined}
        schluessel={(z) => z.id}
        alle="/site-docs"
        leer={t('siteDocs.dashboard.empty')}
        zeile={(z) => (
          <>
            <span className="w-20 shrink-0 text-xs text-muted-foreground">{fmtDate(z.documentDate)}</span>
            <Link to={`/site-docs/${z.id}`} className="min-w-0 flex-1 truncate hover:underline">
              <span className="mr-2 font-mono text-xs text-muted-foreground">{z.number ?? t(`siteDocs.kinds.${z.kind}`)}</span>
              {z.title}
              <span className="ml-2 text-xs text-muted-foreground">{z.projectLabel}</span>
            </Link>
            <StatusPill status={z.status} />
          </>
        )}
      />
    </div>
  )
}
